/**
 * Mapa na żywo — centrum dowodzenia (`lx.*`): zakładki, warstwy, filtry, karta handlowca, alerty, sterowanie mapą, KPI.
 * Rejestrowane w `extra.ts`; `lxEn` jest typowany względem `lxPl`, więc brak klucza to błąd kompilacji.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const lxPl = {
  tabs: { aria: 'Widok mapy', live: 'Na żywo', history: 'Historia' },
  layers: {
    title: 'Warstwy',
    open: 'Warstwy i podkład mapy',
    reps: 'Handlowcy',
    customers: 'Klienci',
    overdueOnly: 'Tylko zaległe',
    trails: 'Ślad dzisiejszy',
    visits: 'Wizyty dziś',
    deliveries: 'Dostawy dziś',
    geofence: 'Geofence aktywnych wizyt',
    heat: 'Aktywność (mapa cieplna)',
    basemap: 'Podkład',
    basemapStandard: 'Standard',
    basemapLight: 'Jasny',
    basemapDark: 'Ciemny',
    customersLoading: 'Ładowanie klientów…',
    customersUnavailable: 'Warstwa klientów niedostępna',
    withoutCoords: '{count} klientów bez współrzędnych',
    historyUnavailable: 'Dane historii dziś niedostępne',
  },
  filters: {
    searchPlaceholder: 'Szukaj handlowca lub klienta…',
    searchLabel: 'Szukaj handlowca lub klienta na mapie',
    repsGroup: 'Handlowcy',
    customersGroup: 'Klienci',
    noMatches: 'Brak dopasowań',
    statusAria: 'Filtr statusu handlowców',
    all: 'Wszyscy',
    visit: 'Na wizycie',
    moving: 'W ruchu',
    stopped: 'Postój',
    nosignal: 'Brak sygnału',
    listEmpty: 'Brak handlowców dla wybranego filtra.',
    clearSearch: 'Wyczyść wyszukiwanie',
  },
  card: {
    close: 'Zamknij kartę',
    movement: { MOVING: 'W ruchu', STOPPED: 'Postój', UNKNOWN: 'Brak sygnału' },
    speed: 'Prędkość',
    accuracy: 'Dokładność',
    signalAge: 'Wiek sygnału',
    todayKm: 'Dziś przejechane',
    noValue: '—',
    speedValue: '{value} km/h',
    accuracyValue: '±{value} m',
    kmValue: '{value} km',
    activeVisit: 'Wizyta w toku',
    visitTimer: 'trwa {duration}',
    nextVisit: 'Następna wizyta',
    nextVisitNone: 'Brak zaplanowanych wizyt',
    nextVisitAt: 'plan. {time}',
    nextVisitDistance: '{distance} w linii prostej',
    visitsToday: 'Wizyty dziś',
    overdueNearby: 'Klienci zaległe w pobliżu',
    overdueNone: 'Brak zaległych klientów w zasięgu',
    overdueNoData: 'Warstwa klientów niedostępna',
    neverVisited: 'bez wizyty',
    daysSince: '{days} dni od wizyty',
    historyBtn: 'Pokaż historię dnia',
    centerBtn: 'Wyśrodkuj',
    openCustomer: 'Otwórz kartę klienta',
    trackOn: 'Śledź',
    trackOff: 'Przestań śledzić',
    noPosition: 'Brak pozycji GPS',
  },
  alerts: {
    title: 'Alerty na żywo',
    none: 'Brak alertów',
    signal: '{rep}: brak sygnału GPS od {age}',
    signalNever: '{rep}: brak jakiegokolwiek sygnału GPS dziś',
    longStop: '{rep}: postój poza wizytą {minutes} min{near}',
    longStopNear: ' (przy {customer})',
    longVisit: '{rep}: wizyta u {customer} trwa {duration}',
    noVisit: '{rep}: brak wizyty do godz. 12:00',
    goTo: 'Pokaż handlowca',
  },
  map: {
    fullscreen: 'Pełny ekran',
    exitFullscreen: 'Wyjdź z pełnego ekranu',
    pause: 'Wstrzymaj odświeżanie',
    resume: 'Wznów odświeżanie',
    paused: 'Odświeżanie wstrzymane',
    refreshIn: 'aktualizacja za {seconds} s',
    refreshing: 'odświeżanie…',
    tracking: 'Śledzenie: {rep}',
    legendCustomer: 'Klient',
    legendOverdue: 'Zaległy',
    overdueCount: 'Zaległe: {count}',
    customerCount: 'Klienci: {count}',
    customerPopupOwner: 'Opiekun: {owner}',
    customerPopupLast: 'Ostatnia wizyta: {value}',
    customerPopupOverdue: 'Zaległy kontakt',
    visitPlanned: 'Zaplanowana',
    visitInProgress: 'W toku',
    visitCompleted: 'Zrealizowana',
    visitMissed: 'Niezrealizowana',
    delivery: 'Dostawa',
    deliveryRoute: 'Trasa {route}',
    trail: 'Ślad dzisiejszy',
    distanceM: '{value} m',
    distanceKm: '{value} km',
    basemapNote: 'Podkłady: OpenStreetMap',
  },
  hero: {
    field: 'W terenie teraz',
    fieldHint: 'z {total} w zakresie',
    moving: 'W ruchu',
    movingHint: 'prędkość > 5 km/h',
    noSignal: 'Brak sygnału',
    noSignalHint: '{none} bez GPS · {stale} bez świeżego pingu',
    noSignalOk: 'wszyscy raportują pozycję',
    km: 'Dystans dziś',
    kmHint: 'suma ze śladów GPS',
    visits: 'Wizyty dziś',
    visitsHint: 'zrealizowane z {planned} zaplanowanych',
    overdue: 'Klienci zaległe',
    overdueHint: 'z {total} na mapie',
    overdueNone: 'brak danych o klientach',
    insightAlerts: 'Aktywne alerty: {count}. Najważniejszy — {text}',
    alertsAction: 'Pokaż',
  },
} as const;

export const lxEn: Shape<typeof lxPl> = {
  tabs: { aria: 'Map view', live: 'Live', history: 'History' },
  layers: {
    title: 'Layers',
    open: 'Layers and basemap',
    reps: 'Reps',
    customers: 'Customers',
    overdueOnly: 'Overdue only',
    trails: "Today's trail",
    visits: "Today's visits",
    deliveries: "Today's deliveries",
    geofence: 'Active-visit geofence',
    heat: 'Activity (heat map)',
    basemap: 'Basemap',
    basemapStandard: 'Standard',
    basemapLight: 'Light',
    basemapDark: 'Dark',
    customersLoading: 'Loading customers…',
    customersUnavailable: 'Customer layer unavailable',
    withoutCoords: '{count} customers without coordinates',
    historyUnavailable: "Today's history data unavailable",
  },
  filters: {
    searchPlaceholder: 'Search rep or customer…',
    searchLabel: 'Search a rep or customer on the map',
    repsGroup: 'Reps',
    customersGroup: 'Customers',
    noMatches: 'No matches',
    statusAria: 'Rep status filter',
    all: 'Everyone',
    visit: 'On a visit',
    moving: 'Moving',
    stopped: 'Stopped',
    nosignal: 'No signal',
    listEmpty: 'No reps match this filter.',
    clearSearch: 'Clear search',
  },
  card: {
    close: 'Close card',
    movement: { MOVING: 'Moving', STOPPED: 'Stopped', UNKNOWN: 'No signal' },
    speed: 'Speed',
    accuracy: 'Accuracy',
    signalAge: 'Signal age',
    todayKm: 'Driven today',
    noValue: '—',
    speedValue: '{value} km/h',
    accuracyValue: '±{value} m',
    kmValue: '{value} km',
    activeVisit: 'Visit in progress',
    visitTimer: 'running {duration}',
    nextVisit: 'Next visit',
    nextVisitNone: 'No planned visits',
    nextVisitAt: 'planned {time}',
    nextVisitDistance: '{distance} as the crow flies',
    visitsToday: 'Visits today',
    overdueNearby: 'Overdue customers nearby',
    overdueNone: 'No overdue customers in range',
    overdueNoData: 'Customer layer unavailable',
    neverVisited: 'never visited',
    daysSince: '{days} days since last visit',
    historyBtn: "Show the day's history",
    centerBtn: 'Center',
    openCustomer: 'Open customer card',
    trackOn: 'Follow',
    trackOff: 'Stop following',
    noPosition: 'No GPS position',
  },
  alerts: {
    title: 'Live alerts',
    none: 'No alerts',
    signal: '{rep}: no GPS signal for {age}',
    signalNever: '{rep}: no GPS signal at all today',
    longStop: '{rep}: stopped outside a visit for {minutes} min{near}',
    longStopNear: ' (near {customer})',
    longVisit: '{rep}: visit at {customer} has been running {duration}',
    noVisit: '{rep}: no visit by 12:00',
    goTo: 'Show rep',
  },
  map: {
    fullscreen: 'Full screen',
    exitFullscreen: 'Exit full screen',
    pause: 'Pause refreshing',
    resume: 'Resume refreshing',
    paused: 'Refreshing paused',
    refreshIn: 'update in {seconds} s',
    refreshing: 'refreshing…',
    tracking: 'Following: {rep}',
    legendCustomer: 'Customer',
    legendOverdue: 'Overdue',
    overdueCount: 'Overdue: {count}',
    customerCount: 'Customers: {count}',
    customerPopupOwner: 'Owner: {owner}',
    customerPopupLast: 'Last visit: {value}',
    customerPopupOverdue: 'Contact overdue',
    visitPlanned: 'Planned',
    visitInProgress: 'In progress',
    visitCompleted: 'Completed',
    visitMissed: 'Missed',
    delivery: 'Delivery',
    deliveryRoute: 'Route {route}',
    trail: "Today's trail",
    distanceM: '{value} m',
    distanceKm: '{value} km',
    basemapNote: 'Basemaps: OpenStreetMap',
  },
  hero: {
    field: 'In the field now',
    fieldHint: 'of {total} in scope',
    moving: 'Moving',
    movingHint: 'speed > 5 km/h',
    noSignal: 'No signal',
    noSignalHint: '{none} without GPS · {stale} without a fresh ping',
    noSignalOk: 'everyone is reporting a position',
    km: 'Distance today',
    kmHint: 'sum of GPS trails',
    visits: 'Visits today',
    visitsHint: 'completed out of {planned} planned',
    overdue: 'Overdue customers',
    overdueHint: 'of {total} on the map',
    overdueNone: 'no customer data',
    insightAlerts: 'Active alerts: {count}. Top one — {text}',
    alertsAction: 'Show',
  },
};
